import {
  Entity,
  GetItemCommand,
  InputItem,
  isExtension,
  number,
  PutItemCommand,
  QueryCommand,
  schema,
  string,
  Table,
} from 'dynamodb-toolbox'
import myzod, { Infer } from 'myzod'
import { JobCategoryEnum } from '../../../generated/graphql'
import { createId } from '../../../helpers/utils'
import {
  ApplicationTable,
  decodeNextToken,
  encodeNextToken,
} from '../../tables/applicationDynamoDbTable'

export const Name = 'JobAuction'

// DynamoDB Toolbox Schema
const baseJobAuctionSchema = schema({
  jobId: string().required().key(),
  id: string()
    .default(() => createId(Name))
    .optional(),

  maxBids: number().default(() => 50),
  city: string().required(),
  jobStartDate: string().required(),
  jobStatTime: string().required(),
  jobCategory: string()
    .enum(...Object.values(JobCategoryEnum))
    .required(),
  userId: string().required(),
  suburb: string().optional(),

  type: string()
    .default(() => Name)
    .optional(),
})

const jobAuctionSchemaWithGsi = baseJobAuctionSchema.and({
  gsi_1_pk: string()
    .hidden()
    .optional()
    .link<typeof baseJobAuctionSchema>(
      ({ jobCategory, city }) => `${jobCategory}#-#${city.toUpperCase()}`,
    ),
  gsi_1_sk: string()
    .hidden()
    .optional()
    .link<typeof baseJobAuctionSchema>(({ jobStartDate }) => jobStartDate),
  gsi_2_pk: string()
    .hidden()
    .optional()
    .link<typeof baseJobAuctionSchema>(({ userId }) => userId),
  gsi_2_sk: string()
    .hidden()
    .optional()
    .link<typeof baseJobAuctionSchema>(
      ({ jobStartDate, jobStatTime }) =>
        `JobAuction#-#${new Date(`${jobStatTime} ${jobStartDate}`).toISOString()}`,
    ),
  gsi_3_pk: string()
    .optional()
    .updateLink<typeof baseJobAuctionSchema>(
      ({ jobCategory, city, suburb }) => {
        if (isExtension(city) || isExtension(suburb)) {
          return undefined
        }
        return `${jobCategory}#-#${city?.toUpperCase()}#-#${suburb?.toUpperCase()}`
      },
    )
    .putLink<typeof baseJobAuctionSchema>(
      ({ jobCategory, city, suburb }) =>
        `${jobCategory}#-#${city.toUpperCase()}#-#${suburb?.toUpperCase()}`,
    )
    .link<typeof baseJobAuctionSchema>(
      ({ jobCategory, city, suburb }) =>
        `${jobCategory}#-#${city.toUpperCase()}#-#${suburb?.toUpperCase()}`,
    ),
  gsi_3_sk: string()
    .optional()
    .link<typeof baseJobAuctionSchema>(({ jobStartDate }) => jobStartDate),

  gsi_9_pk: string().hidden().default(Name).optional(),
  gsi_9_sk: string()
    .hidden()
    .optional()
    .updateLink<typeof baseJobAuctionSchema>(() => {
      const bidCloseTime = new Date()
      bidCloseTime.setDate(bidCloseTime.getDate() + 1)
      return bidCloseTime.toISOString()
    })
    .putLink<typeof baseJobAuctionSchema>(() => {
      const bidCloseTime = new Date()
      bidCloseTime.setDate(bidCloseTime.getDate() + 1)
      return bidCloseTime.toISOString()
    })
    .link<typeof baseJobAuctionSchema>(() => {
      const bidCloseTime = new Date()
      bidCloseTime.setDate(bidCloseTime.getDate() + 1)
      return bidCloseTime.toISOString()
    }),
})

// Myzod Schema for Validation
export const JobAuctionEntitySchema = myzod
  .object({
    jobId: myzod.string().pattern(/^Job#/),
    id: myzod.string().pattern(/^JobAuction#/),
    maxBids: myzod.number(),
    city: myzod.string(),
    jobStartDate: myzod.string(),
    jobStatTime: myzod.string(),
    jobCategory: myzod.enum(JobCategoryEnum),
    userId: myzod.string().pattern(/^User#/),
    suburb: myzod.string().optional(),
  })
  .allowUnknownKeys(true)

export type JobAuctionEntityType = Infer<typeof JobAuctionEntitySchema>

export const getJobAuctionEntity = (table: Table) => {
  return new Entity({
    table,
    name: Name,
    schema: jobAuctionSchemaWithGsi,
    computeKey: ({ jobId }) => ({
      pk: jobId,
      sk: Name,
    }),
  })
}

export const JobAuctionEntity = getJobAuctionEntity(ApplicationTable)

export type JobAuctionInputEntity = InputItem<typeof JobAuctionEntity>

// Upsert Function with Validation
export const upsertJobAuction = async (
  input: JobAuctionInputEntity,
): Promise<JobAuctionInputEntity> => {
  JobAuctionEntitySchema.parse(input) // Validate input with Myzod
  await JobAuctionEntity.build(PutItemCommand).item(input).send()
  return input
}

// Get by ID with Validation
export const getJobAuctionById = async (jobId: string) => {
  const response = await JobAuctionEntity.build(GetItemCommand)
    .key({
      jobId,
    })
    .send()
  if (response?.Item) {
    return JobAuctionEntitySchema.parse(response.Item) // Validate response
  }
  return null
}

// Query by Category and City
export const searchJobAuctionByCategoryAndCity = async (
  input: {
    jobCategory: string
    city: string
    startDate: string
    suburb?: string
  },
  limit?: number,
  nextToken?: string,
  table: Table = ApplicationTable,
): Promise<{
  items: JobAuctionEntityType[]
  nextToken: string | undefined
}> => {
  const { jobCategory, city, startDate, suburb } = input
  const partitionKey = suburb
    ? `${jobCategory}#-#${city.toUpperCase()}#-#${suburb.toUpperCase()}`
    : `${jobCategory}#-#${city.toUpperCase()}`

  const query = table
    .build(QueryCommand)
    .query({
      index: suburb ? 'gsi3' : 'gsi1',
      partition: partitionKey,
      range: { beginsWith: new Date(startDate).toISOString().slice(0, 10) },
    })
    .options({
      limit: limit || 100,
      exclusiveStartKey: decodeNextToken(nextToken),
    })
    .entities(JobAuctionEntity)

  const { Items: jobAuctions = [], LastEvaluatedKey } = await query.send()

  return {
    items: jobAuctions.map((item) => JobAuctionEntitySchema.parse(item)), // Validate each item
    nextToken: LastEvaluatedKey ? encodeNextToken(LastEvaluatedKey) : undefined,
  }
}

export const getJobAuctionsByUserId = async (
  userId: string,
  nextToken?: string,
  limit?: number,
  table: Table = ApplicationTable,
): Promise<{
  items: JobAuctionEntityType[]
  nextToken: string | undefined
}> => {
  const query = table
    .build(QueryCommand)
    .query({
      index: 'gsi2',
      partition: userId,
      range: { beginsWith: 'JobAuction#-#' },
    })
    .options({
      limit: limit || 100,
      exclusiveStartKey: decodeNextToken(nextToken),
    })
  const { Items: jobAuctions = [], LastEvaluatedKey } = await query.send()

  return {
    items: jobAuctions.map((item) => JobAuctionEntitySchema.parse(item)), // Validate each item
    nextToken: LastEvaluatedKey ? encodeNextToken(LastEvaluatedKey) : undefined,
  }
}
